import React, { useState, useEffect, useRef } from 'react'
import ContentRenderer from '../ContentRenderer/ContentRenderer'

// Import from organized shared structure
import InteractiveQuestion from './shared/questions/InteractiveQuestion'
import MultipleResponseQuestion from './shared/questions/MultipleResponseQuestion'
import SequenceQuestion from './shared/questions/SequenceQuestion'
import ShortAnswerQuestion from './shared/questions/ShortAnswerQuestion'
import WorksheetCheck from './shared/questions/WorksheetCheck'
import InteractiveQuiz from './shared/questions/InteractiveQuiz'
import InteractiveExploration from './shared/explorations/InteractiveExploration'
import InteractiveDiscovery from './shared/explorations/InteractiveDiscovery'
import InteractiveVelocityExploration from './shared/explorations/InteractiveVelocityExploration'
import InteractiveParameterExploration from './shared/explorations/InteractiveParameterExploration'
import InteractiveQuantityExploration from './shared/explorations/InteractiveQuantityExploration'
import InteractiveFormula from './shared/formulas/InteractiveFormula'
import InteractiveFormulaDiscovery from './shared/formulas/InteractiveFormulaDiscovery'
import InteractiveFormulaComparison from './shared/formulas/InteractiveFormulaComparison'
import InteractiveOscillation from './shared/simulations/InteractiveOscillation'
import InteractiveVTGraph from './shared/simulations/InteractiveVTGraph'
import RadioactiveDecaySimulation from './shared/simulations/RadioactiveDecaySimulation'
import GasParticleSimulation from './shared/simulations/GasParticleSimulation'
import WavePropagationSimulation from './shared/simulations/WavePropagationSimulation'
import PhaseChangeSimulation from './shared/simulations/PhaseChangeSimulation'
import NuclearReactionSimulation from './shared/simulations/NuclearReactionSimulation'
import AcGeneratorSimulation from './shared/simulations/AcGeneratorSimulation'
import HeatingCurveSimulation from './shared/simulations/HeatingCurveSimulation'
import SpecificHeatSimulation from './shared/simulations/SpecificHeatSimulation'
import CoulombForceSimulation from './shared/simulations/CoulombForceSimulation'
import EnergyOscillationSimulation from './shared/simulations/EnergyOscillationSimulation'
import CollisionSimulation from './shared/simulations/CollisionSimulation'
import ElectricFieldSimulation from './shared/simulations/ElectricFieldSimulation'
import HookeSpringSimulation from './shared/simulations/HookeSpringSimulation'
import MagneticForceSimulation from './shared/simulations/MagneticForceSimulation'
import NucleusStructureSimulation from './shared/simulations/NucleusStructureSimulation'
import CircularMotionSimulation from './shared/simulations/CircularMotionSimulation'
import GasLawSimulation from './shared/simulations/GasLawSimulation'
import MagneticFieldSimulation from './shared/simulations/MagneticFieldSimulation'
import DrivenResonanceSimulation from './shared/simulations/DrivenResonanceSimulation'
import StandingWaveSimulation from './shared/simulations/StandingWaveSimulation'
import WaveInterferenceSimulation from './shared/simulations/WaveInterferenceSimulation'
import MotionGraphsSimulation from './shared/simulations/MotionGraphsSimulation'
import DisplacementTimeSimulation from './shared/simulations/DisplacementTimeSimulation'
import FreeFall3DSimulation from './shared/simulations/FreeFall3DSimulation'
import TwoOscillationsSimulation from './shared/simulations/TwoOscillationsSimulation'
import ResistanceOhmSimulation from './shared/simulations/ResistanceOhmSimulation'
import EMFMeasurementSimulation from './shared/simulations/EMFMeasurementSimulation'
import ProjectileMotionSimulation from './shared/simulations/ProjectileMotionSimulation'
import CircuitSimulation from './shared/simulations/CircuitSimulation'
import VaporizationSimulation from './shared/simulations/VaporizationSimulation'
import EMInductionSimulation from './shared/simulations/EMInductionSimulation'
import VideoSimulation from './shared/simulations/VideoSimulation'
import InclinedPlaneSimulation from './shared/simulations/InclinedPlaneSimulation'
import SpringOscillatorSimulation from './shared/simulations/SpringOscillatorSimulation'
import SimplePendulumSimulation from './shared/simulations/SimplePendulumSimulation'
import ForceVectorSimulation from './shared/simulations/ForceVectorSimulation'
import InteractiveRealWorldExample from './shared/applications/InteractiveRealWorldExample'
import InteractiveProblemSolving from './shared/applications/InteractiveProblemSolving'
import { PhysicsSurvivalRunner } from '../game'
import { LessonStatsService } from '../../services/stats/LessonStatsService'
import { isViewableTextBlock } from './shared/contentBlocks'
import { VideoService } from '../../services/video/VideoService'
import LessonStatsDisplay from '../stats/LessonStatsDisplay'
import QuizPlayer from '../../features/lesson-builder/components/QuizPlayer'

// NOTE: SimulationRef component removed - simulations are now injected automatically
// via ContentService and don't need separate loading

interface InteractiveContentRendererProps {
  content: any
  onProgress?: (progress: number) => void
  onSectionComplete?: () => void // ✅ Callback khi hoàn thành section
  onFirstInteraction?: () => void // ✅ Callback khi học sinh tương tác lần đầu
  lessonId?: string
  blockKeyPrefix?: string // tiền tố khoá khối (vd id thẻ) để ghi "đã xem khối text" không trùng giữa các thẻ
  disableAutoScroll?: boolean // tắt tự cuộn cửa sổ (dùng cho xem trước/viewer — tránh trang tự nhảy)
}

// Câu hỏi CHỐT thật (chỉ những loại này mới quyết định "hoàn thành phần" — KHÔNG tính mô phỏng).
const QUESTION_GATE_TYPES = new Set(['interactive_question', 'multiple_response_question', 'sequence_question', 'worksheet_check'])
const gateIdOf = (type: string, i: number): string =>
  type === 'multiple_response_question' ? `mrq-${i}`
    : type === 'sequence_question' ? `seq-${i}`
    : type === 'worksheet_check' ? `wc-${i}`
    : `question-${i}`

const InteractiveContentRenderer: React.FC<InteractiveContentRendererProps> = ({
  content,
  onProgress,
  onSectionComplete,
  onFirstInteraction, // ✅ Callback khi tương tác lần đầu
  // KHÔNG default 'current-lesson': khi thiếu prop (vd RichCard trong bài giảng),
  // để undefined → LessonStatsService fallback currentLessonId (do startLesson đặt)
  // → thống kê đúng/sai/gợi ý/hỏi AI không bị rơi vào key không tồn tại.
  lessonId,
  blockKeyPrefix,
  disableAutoScroll,
}) => {
  const [completedInteractions, setCompletedInteractions] = useState<Set<string>>(new Set())
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [unlockedUpToIndex, setUnlockedUpToIndex] = useState(0) // Track which items are unlocked
  const [showStatsModal, setShowStatsModal] = useState(false) // 📊 Stats modal state
  const [hasFirstInteraction, setHasFirstInteraction] = useState(false) // ✅ Track tương tác đầu tiên
  const contentRefs = useRef<(HTMLDivElement | null)[]>([])

  // ✅ Handle first interaction - gọi callback khi học sinh tương tác lần đầu
  const handleFirstInteraction = () => {
    if (!hasFirstInteraction && onFirstInteraction) {
      console.log('🎯 [InteractiveContentRenderer] First interaction detected, calling onFirstInteraction');
      setHasFirstInteraction(true);
      onFirstInteraction();
    }
  };

  // 📊 Log section info (don't start lesson here - done in XemBaiHoc)
  useEffect(() => {
    // Count ONLY interactive_question type for stats in current section
    const interactiveQuestions = content.filter((item: any) =>
      item?.type === 'interactive_question'
    )
    const currentSectionQuestions = interactiveQuestions.length

    console.log(`📊 Section loaded for lesson ${lessonId}`)
    console.log(`📊 Current section interactive_question items: ${currentSectionQuestions}`)
    console.log(`📊 Current section all interactive items: ${content.filter((item: any) => item?.type?.startsWith('interactive')).length}`)

    // ✅ FIX: Don't start lesson or set total questions here
    // Both are handled centrally in XemBaiHoc component
  }, [lessonId, content])

  // Find the next interactive item index starting from given index
  const findNextInteractiveIndex = (fromIndex: number): number => {
    for (let i = fromIndex; i < content.length; i++) {
      const item = content[i]
      if (item?.type && (
        item.type.startsWith('interactive') ||
        item.type === 'multiple_response_question' ||
        item.type === 'sequence_question' ||
        item.type === 'worksheet_check'
      )) {
        return i
      }
    }
    return content.length // No more interactive items
  }

  // KHÔNG khoá lộ dần nữa: học sinh xem/cuộn tự do MỌI khối. Câu hỏi vẫn ghi nhận tương tác
  // (mở khoá/tiến độ) như cũ nhưng không chặn hiển thị nội dung phía sau.
  const isItemLocked = (_index: number): boolean => false

  // Initialize refs array + KHÔI PHỤC tương tác đã hoàn thành (đã hydrate từ backend vào LessonStatsService)
  useEffect(() => {
    contentRefs.current = contentRefs.current.slice(0, content.length)

    // Lấy các tương tác ĐÃ HOÀN THÀNH thuộc thẻ này (khoá toàn cục = '<blockKeyPrefix>:<id>')
    const prefix = `${blockKeyPrefix || lessonId || ''}:`
    const seeded = new Set<string>()
    LessonStatsService.getAnsweredKeys(lessonId).forEach((k) => {
      if (k.startsWith(prefix)) seeded.add(k.slice(prefix.length))
    })

    let unlockTo = findNextInteractiveIndex(0)
    if (seeded.size > 0) {
      setCompletedInteractions(seeded)
      // mở khoá tới mục tương tác kế sau CÂU HỎI đã trả lời cuối cùng → nội dung đã học hiện sẵn
      let lastIdx = -1
      content.forEach((it: any, i: number) => {
        if (QUESTION_GATE_TYPES.has(it?.type) && seeded.has(gateIdOf(it.type, i))) lastIdx = i
      })
      if (lastIdx >= 0) unlockTo = Math.max(unlockTo, findNextInteractiveIndex(lastIdx + 1))
    } else {
      setCompletedInteractions(new Set())
    }
    setUnlockedUpToIndex(unlockTo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, blockKeyPrefix, lessonId])

  // Intersection Observer to detect when items are viewed
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = contentRefs.current.indexOf(entry.target as HTMLDivElement)

          if (entry.isIntersecting && index !== -1 && !isItemLocked(index)) {
            // Update current item if this is further ahead
            if (index > currentItemIndex) {
              setCurrentItemIndex(index)
            }
            // Ghi nhận "đã xem khối text" (cuộn tới) → tính tiến độ hoàn thành theo từng mục.
            const it = content[index]
            if (isViewableTextBlock(it)) {
              LessonStatsService.recordTextViewed(`${blockKeyPrefix || lessonId || ''}:${index}`, lessonId)
            }
          }
        })
      },
      {
        threshold: 0.3, // Consider viewed when 30% visible
        rootMargin: '-50px 0px -50px 0px'
      }
    )

    contentRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => {
      observer.disconnect()
    }
  }, [content, currentItemIndex, unlockedUpToIndex])

  const scrollToNextItem = (currentIndex: number) => {
    const nextIndex = currentIndex + 1
    if (nextIndex < content.length && contentRefs.current[nextIndex]) {
      // Smooth scroll to next item with offset for sticky headers
      // Calculate dynamic header height for mobile/desktop
      const isMobile = window.innerWidth < 768
      const stickyHeaderHeight = isMobile ? 120 : 200 // Mobile has smaller headers

      const element = contentRefs.current[nextIndex]
      if (!element) return

      // Use requestAnimationFrame for smoother animation
      requestAnimationFrame(() => {
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - stickyHeaderHeight

        // Use smooth scroll with easing
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      })

      setCurrentItemIndex(nextIndex)
    }
  }

  // ✅ Hàm đếm tổng câu hỏi tương tác (giống XemBaiHoc)
  const countTotalInteractiveQuestions = (items: any[]): number => {
    let count = 0
    items.forEach((item: any) => {
      if (
        item?.type === 'interactive_question' ||
        item?.type === 'multiple_response_question' ||
        item?.type === 'sequence_question' ||
        item?.type === 'worksheet_check'
      ) {
        count++
      }
      // Đếm câu hỏi trong simulation
      else if (item?.type === 'interactive_vt_graph' && item?.interactiveQuestions?.length > 0) {
        count += item.interactiveQuestions.length
      }
    })
    return count
  }

  const handleInteractionComplete = (id: string, itemIndex: number) => {
    console.log(`🔓 handleInteractionComplete called: id=${id}, itemIndex=${itemIndex}`)

    const newCompleted = new Set(completedInteractions)
    newCompleted.add(id)
    setCompletedInteractions(newCompleted)

    // Lưu khoá toàn cục để khôi phục khi load lại (sẽ được đẩy lên backend qua meta_data.state)
    LessonStatsService.recordAnswered(`${blockKeyPrefix || lessonId || ''}:${id}`, lessonId)

    console.log(`🔓 Completed interactions:`, Array.from(newCompleted))

    // ✅ Đếm đúng tổng số câu hỏi (bao gồm cả trong simulation)
    const totalInteractive = countTotalInteractiveQuestions(content)
    const completedCount = newCompleted.size

    console.log(`🔓 Progress: ${completedCount}/${totalInteractive} = ${(completedCount / totalInteractive) * 100}%`)

    // Calculate percentage based on interactive items completed
    const progressPercentage = totalInteractive > 0
      ? (completedCount / totalInteractive) * 100
      : 0

    // Report progress immediately
    onProgress?.(progressPercentage)

    const nextInteractiveIndex = findNextInteractiveIndex(itemIndex + 1)
    console.log(`🔓 Next interactive index: ${nextInteractiveIndex}`)

    // Chỉ CÂU HỎI CHỐT mới quyết định hoàn thành/cuộn. Mô phỏng tự chạy KHÔNG được
    // tính là "đã trả lời" → tránh đánh dấu hoàn thành sai & tránh trang tự trượt xuống.
    const isQuestion = QUESTION_GATE_TYPES.has(content[itemIndex]?.type)

    // 📊 Hoàn thành phần CHỈ khi đã trả lời HẾT câu hỏi chốt (không tính mô phỏng).
    const gateIdxs = content
      .map((it: any, i: number) => (QUESTION_GATE_TYPES.has(it?.type) ? i : -1))
      .filter((i: number) => i >= 0)
    const allGatesDone =
      gateIdxs.length > 0 && gateIdxs.every((i: number) => newCompleted.has(gateIdOf(content[i].type, i)))
    if (allGatesDone) {
      console.log(`🎉 Section completed (all gates answered)! Notifying parent...`)
      onSectionComplete?.()
    }

    // Mở khoá mục kế (cho cả mô phỏng để nội dung lộ tiếp). Tự CUỘN chỉ sau khi trả lời CÂU HỎI,
    // và chỉ khi không tắt (xem trước/viewer tắt để khỏi nhảy trang).
    if (isQuestion) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          if (!disableAutoScroll) scrollToNextItem(itemIndex)
          setTimeout(() => setUnlockedUpToIndex(nextInteractiveIndex), 400)
        })
      }, 1000) // Giữ kết quả 1 giây trước khi cuộn
    } else {
      // Mô phỏng/khối tương tác khác: chỉ mở khoá tiếp, KHÔNG cuộn.
      setUnlockedUpToIndex((prev) => Math.max(prev, nextInteractiveIndex))
    }
  }

  const renderInteractiveContent = (item: any, index: number) => {
    // Debug: Log để xem cấu trúc dữ liệu
    console.log('Rendering item:', item)

    if (!item || !item.type) {
      return (
        <div key={`no-type-${index}`} className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">Lỗi: Item không có type</p>
          <pre className="text-xs mt-2">{JSON.stringify(item, null, 2)}</pre>
        </div>
      )
    }

    switch (item.type) {
      // NOTE: simulation_ref type is deprecated and filtered out by ContentService
      // Simulations are now injected automatically with their actual types
      case 'simulation_ref':
        // This should never happen as ContentService filters these out
        console.warn('⚠️ Encountered simulation_ref - should be filtered by ContentService', item)
        return (
          <div key={`deprecated-${index}`} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800">⚠️ Legacy simulation reference (deprecated)</p>
            <p className="text-sm text-yellow-600">This item should have been filtered by ContentService</p>
          </div>
        )

      // New interactive components
      case 'interactive_oscillation':
        return (
          <InteractiveOscillation
            key={`${item.type}-${index}`}
            title={item.title || "Mô phỏng dao động điều hòa"}
            description={item.description || "Quan sát chuyển động dao động điều hòa"}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`oscillation-${index}`, index)
            }}
          />
        )

      case 'interactive_vt_graph':
        return (
          <InteractiveVTGraph
            key={`${item.type}-${index}`}
            title={item.title || "Đồ thị v-t trong chuyển động biến đổi đều"}
            description={item.description || "Kéo thanh trượt để thay đổi gia tốc"}
            interactiveQuestions={item.interactiveQuestions || []}
            lessonId={lessonId}
            onQuestionComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`vt-graph-q-${index}`, index)
            }}
          />
        )

      case 'interactive_radioactive_decay':
        return (
          <RadioactiveDecaySimulation
            key={`${item.type}-${index}`}
            title={item.title}
            description={item.description}
            onComplete={() => {
              handleFirstInteraction();
              handleInteractionComplete(`radioactive-${index}`, index)
            }}
          />
        )

      case 'interactive_gas_particles':
        return (
          <GasParticleSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`gas-${index}`, index) }} />
        )

      case 'interactive_wave':
        return (
          <WavePropagationSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`wave-${index}`, index) }} />
        )

      case 'interactive_phase_change':
        return (
          <PhaseChangeSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`phase-${index}`, index) }} />
        )

      case 'interactive_nuclear_reaction':
        return (
          <NuclearReactionSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`nuclear-${index}`, index) }} />
        )

      case 'interactive_ac_generator':
        return (
          <AcGeneratorSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`acgen-${index}`, index) }} />
        )

      case 'interactive_heating_curve':
        return (
          <HeatingCurveSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`heat-${index}`, index) }} />
        )

      case 'interactive_specific_heat':
        return (
          <SpecificHeatSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`spec-${index}`, index) }} />
        )

      case 'interactive_coulomb':
        return (
          <CoulombForceSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`coulomb-${index}`, index) }} />
        )

      case 'interactive_energy_oscillation':
        return (
          <EnergyOscillationSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`enosc-${index}`, index) }} />
        )

      case 'interactive_collision':
        return (
          <CollisionSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`collide-${index}`, index) }} />
        )

      case 'interactive_electric_field':
        return (
          <ElectricFieldSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`efield-${index}`, index) }} />
        )

      case 'interactive_hooke':
        return (
          <HookeSpringSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`hooke-${index}`, index) }} />
        )

      case 'interactive_inclined_plane':
        return (
          <InclinedPlaneSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`incline-${index}`, index) }} />
        )

      case 'interactive_chuyen_dong_brown_3d':
      case 'interactive_dinh_luat_boyle_3d':
      case 'interactive_dong_co_hoi_nuoc_3d': {
        const filePathMap: Record<string, string> = {
          'interactive_chuyen_dong_brown_3d': '/3d-models/chuyen-dong-brown-3d.html',
          'interactive_dinh_luat_boyle_3d': '/3d-models/dinh-luat-boyle-3d.html',
          'interactive_dong_co_hoi_nuoc_3d': '/3d-models/dong-co-hoi-nuoc-3d.html'
        }
        const src = filePathMap[item.type]
        const height = item.height || 550
        return (
          <div key={`${item.type}-${index}`} className="w-full rounded-xl overflow-hidden border border-gray-200 bg-white" style={{ height }}>
            <iframe
              title={item.title}
              src={src}
              className="w-full h-full border-0"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-popups"
              onLoad={() => {
                handleFirstInteraction()
                handleInteractionComplete(`${item.type}-${index}`, index)
              }}
            />
          </div>
        )
      }

      case 'interactive_spring_oscillator':
        return (
          <SpringOscillatorSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`springosc-${index}`, index) }} />
        )

      case 'interactive_simple_pendulum':
        return (
          <SimplePendulumSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`pendulum-${index}`, index) }} />
        )

      case 'interactive_force_vectors':
        return (
          <ForceVectorSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`forcevec-${index}`, index) }} />
        )

      case 'interactive_magnetic_force':
        return (
          <MagneticForceSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`magforce-${index}`, index) }} />
        )

      case 'interactive_nucleus_structure':
        return (
          <NucleusStructureSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`nucleus-${index}`, index) }} />
        )

      case 'interactive_circular_motion':
        return (
          <CircularMotionSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`circ-${index}`, index) }} />
        )

      case 'interactive_gas_law':
        return (
          <GasLawSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`gaslaw-${index}`, index) }} />
        )

      case 'interactive_magnetic_field':
        return (
          <MagneticFieldSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`magfield-${index}`, index) }} />
        )

      case 'interactive_damped_resonance':
        return (
          <DrivenResonanceSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`resonance-${index}`, index) }} />
        )

      case 'interactive_standing_wave':
        return (
          <StandingWaveSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`standwave-${index}`, index) }} />
        )

      case 'interactive_wave_interference':
        return (
          <WaveInterferenceSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`interference-${index}`, index) }} />
        )

      case 'interactive_resistance_ohm':
        return (
          <ResistanceOhmSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`resohm-${index}`, index) }} />
        )

      case 'interactive_emf_measurement':
        return (
          <EMFMeasurementSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`emf-${index}`, index) }} />
        )

      case 'interactive_two_oscillations':
        return (
          <TwoOscillationsSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`twoosc-${index}`, index) }} />
        )

      case 'interactive_freefall_3d':
        return (
          <FreeFall3DSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`freefall-${index}`, index) }} />
        )

      case 'interactive_displacement_time':
        return (
          <DisplacementTimeSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`disptime-${index}`, index) }} />
        )

      case 'interactive_motion_graphs':
        return (
          <MotionGraphsSimulation key={`${item.type}-${index}`} title={item.title} description={item.description} mode={item.mode}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`motiongraphs-${index}`, index) }} />
        )

      case 'interactive_projectile_motion':
        return (
          <ProjectileMotionSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`projectile-${index}`, index) }} />
        )

      case 'interactive_circuit':
        return (
          <CircuitSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`circuit-${index}`, index) }} />
        )

      case 'interactive_vaporization':
        return (
          <VaporizationSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`vapor-${index}`, index) }} />
        )

      case 'interactive_em_induction':
        return (
          <EMInductionSimulation key={`${item.type}-${index}`} title={item.title} description={item.description}
            onComplete={() => { handleFirstInteraction(); handleInteractionComplete(`induction-${index}`, index) }} />
        )

      case 'video_simulation':
      case 'simulation':
      case 'interactive_simulation':
      case 'mo_phong':
        return (
          <VideoSimulation
            key={`${item.type}-${index}`}
            title={item.title || "Mô phỏng bằng video"}
            description={item.description || "Video mô phỏng để hiểu rõ hơn về nội dung bài học"}
            videoPath={item.videoPath || item.url || item.data?.videoPath || `${VideoService.getVideoBaseUrl()}/${item.id || 'default'}.mp4`}
            onProgress={(progress) => {
              // ✅ Check student info on first interaction (when video starts playing)
              if (progress > 0.1) { // When video starts (10% progress)
                handleFirstInteraction();
              }
              if (progress > 0.8) { // Consider 80% progress as complete
                handleInteractionComplete(`video-${index}`, index)
              }
            }}
          />
        )

      case 'interactive_formula':
        return (
          <InteractiveFormula
            key={`${item.type}-${index}`}
            title={item.title || "Công thức tương tác"}
            formula={item.formula || ""}
            variables={item.variables || []}
            resultFormula={item.resultFormula || ""}
            resultUnit={item.resultUnit || ""}
            description={item.description}
            insights={item.insights}
            onCalculate={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`formula-${index}`, index)
            }}
          />
        )

      // Enhanced existing components
      case 'interactive_question':
        const isQuestionCompleted = completedInteractions.has(`question-${index}`)
        return (
          <InteractiveQuestion
            key={`${item.type}-${index}`} // ✅ FIX: Stable key để không bị re-mount
            question={item.question || item.data?.question || "Câu hỏi tương tác"}
            options={item.options || item.data?.options || ["Đáp án A", "Đáp án B"]}
            correctAnswer={item.correctAnswer || item.data?.correctAnswer || 0}
            explanation={item.explanation || item.data?.explanation || "Giải thích"}
            hint={item.hint || item.data?.hint}
            image={item.image || item.data?.image} // ✅ Support question images
            imageAlt={item.imageAlt || item.data?.imageAlt} // ✅ Alt text for images
            difficulty={item.difficulty || item.data?.difficulty || 'medium'}
            isCompleted={isQuestionCompleted} // ✅ Pass completion status
            lessonId={lessonId} // ✅ Pass lesson ID for stats tracking
            onAnswer={(isCorrect) => {
              console.log(`🎯 InteractiveQuestion callback: isCorrect=${isCorrect}, index=${index}`)
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              if (isCorrect) {
                handleInteractionComplete(`question-${index}`, index)
              }
            }}
          />
        )

      case 'multiple_response_question':
        const isMRQCompleted = completedInteractions.has(`mrq-${index}`)
        return (
          <MultipleResponseQuestion
            key={`${item.type}-${index}`}
            question={item.question || "Câu hỏi chọn nhiều đáp án"}
            options={item.options || ["Đáp án A", "Đáp án B"]}
            correctAnswers={item.correctAnswers || [0]}
            explanation={item.explanation || "Giải thích"}
            hint={item.hint}
            difficulty={item.difficulty || 'medium'}
            lessonId={lessonId}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`mrq-${index}`, index)
            }}
          />
        )

      case 'sequence_question':
        const isSeqCompleted = completedInteractions.has(`seq-${index}`)
        return (
          <SequenceQuestion
            key={`${item.type}-${index}`}
            question={item.question || "Sắp xếp các mục theo đúng thứ tự"}
            items={item.items || ["Mục 1", "Mục 2", "Mục 3"]}
            correctSequence={item.correctSequence || [0, 1, 2]}
            explanation={item.explanation || "Giải thích"}
            hint={item.hint}
            difficulty={item.difficulty || 'medium'}
            lessonId={lessonId}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`seq-${index}`, index)
            }}
          />
        )

      case 'short_answer':
        // Câu tự luận ngắn trong phiếu học tập: HS gõ trả lời (lưu server) + xem đáp án gợi ý + Askbot.
        // KHÔNG phải "gate" hoàn thành (mở, không auto-chấm).
        return (
          <ShortAnswerQuestion
            key={`${item.type}-${index}`}
            question={item.question || item.data?.question || 'Câu hỏi tự luận'}
            sampleAnswer={item.sampleAnswer || item.answer || item.data?.sampleAnswer}
            hint={item.hint || item.data?.hint}
            lessonId={lessonId}
            itemKey={`${blockKeyPrefix || lessonId || ''}:sa-${index}`}
          />
        )

      case 'worksheet_check':
        // Cuối mỗi phiếu học tập: HS tự đánh giá Đạt/Chưa → "Đạt" tính vào tiến độ.
        return (
          <WorksheetCheck
            key={`${item.type}-${index}`}
            title={item.title || item.data?.title}
            lessonId={lessonId}
            itemKey={`${blockKeyPrefix || lessonId || ''}:wc-${index}`}
            onComplete={() => {
              handleFirstInteraction()
              handleInteractionComplete(`wc-${index}`, index)
            }}
          />
        )

      case 'interactive_exercise':
        // For now, treat interactive_exercise like interactive_question if it has question/options
        // Otherwise, render as regular exercise
        if (item.question && item.options) {
          const isExerciseCompleted = completedInteractions.has(`exercise-${index}`)
          return (
            <InteractiveQuestion
              key={`${item.type}-${index}`} // ✅ FIX: Stable key để không bị re-mount
              question={item.question}
              options={item.options}
              correctAnswer={item.correctAnswer || 0}
              explanation={item.explanation || "Giải thích"}
              hint={item.hint}
              image={item.image} // ✅ Support question images
              imageAlt={item.imageAlt} // ✅ Alt text for images
              difficulty={item.difficulty || 'medium'}
              isCompleted={isExerciseCompleted} // ✅ Pass completion status
              lessonId={lessonId} // ✅ Pass lesson ID for stats tracking
              onAnswer={(isCorrect) => {
                console.log(`🎯 InteractiveExercise callback: isCorrect=${isCorrect}, index=${index}`)
                // ✅ Check student info on first interaction
                handleFirstInteraction();
                if (isCorrect) {
                  handleInteractionComplete(`exercise-${index}`, index)
                }
              }}
            />
          )
        }
        return <ContentRenderer key={`regular-${index}`} content={[item]} />

      case 'interactive_exploration':
        return (
          <InteractiveExploration
            key={`${item.type}-${index}`}
            title={item.title || item.data?.title || "Khám phá tương tác"}
            description={item.description || item.data?.description || "Khám phá kiến thức"}
            steps={item.steps || item.data?.steps || []}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`exploration-${index}`, index)
            }}
          />
        )

      case 'interactive_discovery':
        return (
          <InteractiveDiscovery
            key={`${item.type}-${index}`}
            data={item}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`discovery-${index}`, index)
            }}
          />
        )

      case 'interactive_velocity_exploration':
        return (
          <InteractiveVelocityExploration
            key={`${item.type}-${index}`}
            data={item}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`velocity-${index}`, index)
            }}
          />
        )

      case 'interactive_formula_discovery':
        return (
          <InteractiveFormulaDiscovery
            key={`${item.type}-${index}`}
            data={item}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`formula-discovery-${index}`, index)
            }}
          />
        )

      case 'interactive_parameter_exploration':
        return (
          <InteractiveParameterExploration
            key={`${item.type}-${index}`}
            data={item}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`parameter-${index}`, index)
            }}
          />
        )

      case 'interactive_formula_comparison':
        return (
          <InteractiveFormulaComparison
            key={`${item.type}-${index}`}
            data={item}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`comparison-${index}`, index)
            }}
          />
        )

      case 'interactive_quantity_exploration':
        return (
          <InteractiveQuantityExploration
            key={`${item.type}-${index}`}
            data={item}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`quantity-${index}`, index)
            }}
          />
        )

      case 'interactive_real_world_example':
        return (
          <InteractiveRealWorldExample
            key={`${item.type}-${index}`}
            data={item}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`real-world-${index}`, index)
            }}
          />
        )

      case 'interactive_problem_solving':
        return (
          <InteractiveProblemSolving
            key={`${item.type}-${index}`}
            data={item}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`problem-${index}`, index)
            }}
          />
        )

      case 'interactive_quiz':
        return (
          <InteractiveQuiz
            key={`${item.type}-${index}`}
            data={item}
            onComplete={() => {
              // ✅ Check student info on first interaction
              handleFirstInteraction();
              handleInteractionComplete(`quiz-${index}`, index)
            }}
          />
        )

      case 'quiz':
        // Khối quiz (AI sinh ở phần kết thúc): chấm TỪNG CÂU bằng nút "Kiểm tra đáp án"
        // + ghi LessonStatsService — thay cho hiển thị tĩnh qua ContentRenderer trước đây.
        return (
          <QuizPlayer
            key={`quiz-player-${index}`}
            card={{ id: `quiz-${index}`, title: item.title, questions: item.questions || [] }}
          />
        )

      /*
      case 'physics_survival_runner':
      case 'survival_runner':
      case 'minigame_survival':
        // Extract lesson info from item or use defaults
        const lop = item.lop || item.data?.lop || 10
        const chuong = item.chuong || item.data?.chuong || 1
        const bai = item.bai || item.data?.bai || 1
        const minQuestions = item.minQuestions || item.data?.minQuestions || 6
        const maxQuestions = item.maxQuestions || item.data?.maxQuestions || 10
        
        return (
          <div key={`${item.type}-${index}`} className="my-4 sm:my-6">
            <PhysicsSurvivalRunner
              lop={lop}
              chuong={chuong}
              bai={bai}
              minQuestions={minQuestions}
              maxQuestions={maxQuestions}
              onExit={() => {
                // ✅ Check student info on first interaction
                handleFirstInteraction();
                handleInteractionComplete(`survival-runner-${index}`, index)
              }}
            />
          </div>
        )
      */

      // Handle regular content types
      case 'text':
      case 'heading':
      case 'list':
      case 'formula':
      case 'section':
      case 'exercise':
      case 'example':
      case 'image':
      case 'table':
      case 'concept_card':
      case 'formula_card':
      case 'data_table':
      case 'conclusion_card':
        return <ContentRenderer key={`regular-${index}`} content={[item]} />

      default:
        // For non-interactive content types (text, image, heading, table, example, etc.),
        // use ContentRenderer to handle them
        console.log(`📝 Non-interactive content type: ${item.type}, delegating to ContentRenderer`)
        return <ContentRenderer key={`regular-${index}`} content={[item]} />
    }
  }

  // Calculate overall progress based on completed interactions only
  // Count only interactive items
  const interactiveItems = content.filter((item: any) =>
    item?.type && item.type.startsWith('interactive')
  )
  const totalInteractive = interactiveItems.length
  const completedCount = completedInteractions.size
  const progressPercentage = totalInteractive > 0
    ? (completedCount / totalInteractive) * 100
    : 0

  // No automatic progress update on scroll - only update when user interacts

  return (
    <div className="relative">
      {/* Interactive guide removed - users understand intuitively */}

      {/* Progress bar removed - only show overall progress at top level */}

      {/* Render content with refs - Minimal Spacing */}
      <div className="space-y-2 sm:space-y-4">
        {content.map((item: any, index: number) => {
          const locked = isItemLocked(index)
          const isInteractive = item?.type && item.type.startsWith('interactive')

          return (
            <div
              key={`content-${index}`}
              ref={(el) => (contentRefs.current[index] = el)}
              className={`relative ${index === currentItemIndex
                ? 'ring-2 ring-blue-400 ring-opacity-50 rounded-lg'
                : ''
                } ${locked ? 'pointer-events-none' : ''
                }`}
              style={{
                // ✅ GPU acceleration for smoother transitions
                willChange: locked ? 'opacity, transform' : 'auto',
                // ✅ CSS containment to isolate re-renders
                contain: 'layout style paint'
              }}
            >
              {/* Locked overlay - Optimized for performance */}
              {locked && (
                <div
                  className="absolute inset-0 z-10 bg-white/95 rounded flex items-center justify-center transition-opacity duration-300"
                  style={{
                    // ✅ Remove expensive backdrop-blur on desktop for performance
                    backdropFilter: window.innerWidth < 768 ? 'blur(2px)' : 'none'
                  }}
                >
                  <div className="text-3xl sm:text-4xl animate-pulse">🔒</div>
                </div>
              )}

              {/* Content - Remove blur for better performance, use opacity instead */}
              <div
                className="transition-opacity duration-300"
                style={{
                  opacity: locked ? 0.4 : 1,
                  // ✅ GPU acceleration
                  transform: 'translateZ(0)'
                }}
              >
                {!locked && item?.audioUrl && (
                  <div className="mb-2 rounded-lg border border-orange-100 bg-orange-50/60 p-2 flex items-center gap-2">
                    <span className="text-sm shrink-0" aria-hidden>🎧</span>
                    <span className="text-[11px] font-bold text-orange-800 shrink-0">Nghe giảng</span>
                    <audio controls preload="none" src={item.audioUrl} className="flex-1 min-w-0 h-8" />
                  </div>
                )}
                {renderInteractiveContent(item, index)}
              </div>
            </div>
          )
        })}
      </div>

      {/* Completion message removed for cleaner interface */}

      {/* 📊 Progress Indicator - Always show when started */}
      {(() => {
        const stats = LessonStatsService.getStats(lessonId)
        const totalAnswered = stats ? (stats.correctAnswers + stats.wrongAnswers) : 0
        const totalQuestions = stats ? stats.totalQuestions : 0

        // Hiện khi đã bắt đầu làm
        if (totalAnswered === 0 || totalQuestions === 0) return null

        const percentage = Math.round((totalAnswered / totalQuestions) * 100)
        const isComplete = totalAnswered >= totalQuestions

        return (
          <div className="fixed bottom-6 right-6 z-40">
            {/* Chỉ số tiến độ - luôn hiện */}
            <div className={`px-4 py-2 rounded-full shadow-lg transition-all duration-300 ${isComplete
              ? 'bg-green-500 text-white'
              : 'bg-blue-500 text-white'
              }`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {totalAnswered}/{totalQuestions}
                </span>
                {isComplete && <span>✅</span>}
              </div>
            </div>

            {/* Nút thống kê - chỉ hiện khi hoàn thành */}
            {isComplete && (
              <button
                onClick={() => setShowStatsModal(true)}
                className="mt-2 w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                title="Xem thống kê chi tiết"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="text-sm font-medium">Xem thống kê</span>
              </button>
            )}
          </div>
        )
      })()}

      {/* 📊 Stats Modal */}
      {showStatsModal && (() => {
        const stats = LessonStatsService.getStats(lessonId)
        if (!stats) {
          console.log(`❌ No stats found for lessonId: ${lessonId}`)
          return null
        }

        console.log(`📊 Showing stats modal for lessonId: ${lessonId}`, stats)

        return (
          <LessonStatsDisplay
            stats={stats}
            duration={LessonStatsService.getDuration(lessonId)}
            accuracy={LessonStatsService.getAccuracy(lessonId)}
            progress={LessonStatsService.getProgress(lessonId)}
            onClose={() => setShowStatsModal(false)}
          />
        )
      })()}
    </div>
  )
}

export default InteractiveContentRenderer
